import * as React from 'react';
import { Copy, Plus, Search, Undo2 } from 'lucide-react';
import {
  useEnderecosInventario,
  useProdutosMontador,
  type EnderecoOcupado,
  type EscopoInventario,
} from '@/hooks/inventario';
import { cn, numero, plural } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * As duas abas do montador que partem de outra coisa que não o mapa: de um
 * PRODUTO (onde ele está no centro) ou de um ESCOPO já feito (repetir, ou só
 * o que faltou). Tudo cai na mesma seleção de endereços — o escopo do PBS é
 * de endereços, e a função 30 põe os produtos deles ao gravar.
 */

interface PropsSelecao {
  selecao: Set<number>;
  ocupados: Map<number, EnderecoOcupado>;
  aplicar: (ids: Iterable<number>, modo: 'add' | 'remove') => void;
}

function usarAtraso<T>(valor: T, ms: number): T {
  const [v, definir] = React.useState(valor);
  React.useEffect(() => {
    const t = window.setTimeout(() => definir(valor), ms);
    return () => window.clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function AbaProdutos({
  inventario,
  centro,
  selecao,
  ocupados,
  aplicar,
}: PropsSelecao & { inventario: number; centro: number | null }): JSX.Element {
  const [busca, definirBusca] = React.useState('');
  const q = usarAtraso(busca.trim(), 350);
  const consulta = useProdutosMontador(inventario, centro, q);
  const produtos = consulta.data?.produtos ?? [];
  const todos = [...new Set(produtos.flatMap((p) => p.enderecos.map((e) => e.localizador)))];
  const novosTodos = todos.filter((l) => !selecao.has(l)).length;

  return (
    <div className="space-y-3">
      <p className="text-sm text-textoFraco">
        Procure por SKU, EAN, descrição ou marca. Entram os endereços deste centro onde o produto tem saldo —
        e, ao gravar, todos os produtos desses endereços, como no PBS.
      </p>
      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
        <input
          autoFocus
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="Ex.: 25079, 7891234567890, soundbar, TCL"
          className="min-h-toque w-full rounded-lg border border-borda bg-superficie pl-9 pr-3 text-sm focus:border-primaria focus:outline-none"
        />
      </label>
      {q.length < 2 ? null : consulta.isLoading ? (
        <div className="space-y-1.5">
          {[0, 1, 2].map((k) => (
            <Esqueleto key={k} className="h-12" />
          ))}
        </div>
      ) : produtos.length === 0 ? (
        <p className="py-4 text-center text-sm text-textoSuave">Nenhum produto com saldo neste centro.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-textoFraco">
              {numero(produtos.length)} {plural(produtos.length, 'produto', 'produtos')} em {numero(todos.length)}{' '}
              {plural(todos.length, 'endereço', 'endereços')}
            </p>
            <Botao
              variante="secundaria"
              tamanho="pequeno"
              disabled={novosTodos === 0}
              onClick={() => aplicar(todos, 'add')}
            >
              <Plus className="h-4 w-4" />
              Adicionar todos ({numero(novosTodos)})
            </Botao>
          </div>
          <ul className="max-h-[52vh] divide-y divide-borda overflow-y-auto rounded-lg border border-borda">
            {produtos.map((p) => {
              const locs = p.enderecos.map((e) => e.localizador);
              const disp = locs;
              const marcados = disp.filter((l) => selecao.has(l)).length;
              // Só aviso: o endereço pode estar em quantos escopos precisar.
              const bloq = locs.filter((l) => ocupados.has(l)).length;
              const cheio = disp.length > 0 && marcados === disp.length;
              const saldo = p.enderecos.reduce((s, e) => s + e.saldo, 0);
              return (
                <li key={p.produto} className="flex items-center gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium" title={p.descricao ?? ''}>
                      {p.descricao}
                    </p>
                    <p className="text-2xs text-textoFraco">
                      <span className="font-mono">SKU {p.produto}</span>
                      {p.marca ? ` · ${p.marca}` : ''} · {numero(saldo)} un em {numero(locs.length)}{' '}
                      {plural(locs.length, 'endereço', 'endereços')}
                      {bloq > 0 ? <span className="text-status-atencao"> · {numero(bloq)} em outro escopo</span> : null}
                    </p>
                  </div>
                  <Botao
                    variante={cheio ? 'primaria' : 'fantasma'}
                    tamanho="pequeno"
                    disabled={disp.length === 0}
                    onClick={() => aplicar(disp, cheio ? 'remove' : 'add')}
                  >
                    {cheio ? 'Marcado' : marcados > 0 ? `${marcados}/${disp.length}` : `+ ${disp.length}`}
                  </Botao>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/** A última etapa fechada do escopo, para saber o que "faltou". */
function ultimaContagemFechada(e: EscopoInventario): 0 | 1 | 2 | 3 {
  if (e.finalFechadoEm || e.integradoEm) return 3;
  if (e.recontagemFechadaEm) return 2;
  if (e.contagemFechadaEm) return 1;
  return 0;
}

export function AbaDeEscopo({
  inventario,
  escopos,
  selecao,
  aplicar,
}: PropsSelecao & { inventario: number; escopos: EscopoInventario[] }): JSX.Element {
  const enderecos = useEnderecosInventario(inventario);
  const porEscopo = React.useMemo(() => {
    const m = new Map<number, NonNullable<typeof enderecos.data>['enderecos']>();
    for (const e of enderecos.data?.enderecos ?? []) {
      const l = m.get(e.escopo) ?? [];
      l.push(e);
      m.set(e.escopo, l);
    }
    return m;
  }, [enderecos.data]);

  if (enderecos.isLoading) return <Esqueleto className="h-40" />;
  const lista = [...escopos].sort((a, b) => b.escopo - a.escopo);
  if (lista.length === 0) {
    return <p className="py-6 text-center text-sm text-textoSuave">Este inventário ainda não tem escopo.</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-textoFraco">
        Repita um escopo deste inventário ou monte o <b>escopo do que faltou</b>: endereço com saldo que nunca foi
        bipado e o que ficou divergente na última contagem fechada.
      </p>
      <ul className="divide-y divide-borda rounded-lg border border-borda">
        {lista.map((e) => {
          const ends = porEscopo.get(e.escopo) ?? [];
          const todos = ends.map((x) => x.localizador);
          const n = ultimaContagemFechada(e);
          const faltou = ends
            .filter(
              (x) =>
                (x.comSaldo === true && !x.bipado.some(Boolean)) || (n > 0 && x.divergente[n - 1] === true),
            )
            .map((x) => x.localizador);
          const tTodos = todos;
          const tFaltou = faltou;
          const novos = (ls: number[]): number => ls.filter((l) => !selecao.has(l)).length;
          return (
            <li key={e.escopo} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Escopo {e.escopo}</p>
                <p className="text-2xs text-textoFraco">
                  {numero(todos.length)} endereços · {e.ruas.slice(0, 6).join(', ')}
                  {e.ruas.length > 6 ? '…' : ''}
                </p>
              </div>
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                disabled={novos(tTodos) === 0}
                onClick={() => aplicar(tTodos, 'add')}
                title="Os mesmos endereços deste escopo"
              >
                <Copy className="h-3.5 w-3.5" />
                Repetir ({numero(novos(tTodos))})
              </Botao>
              <Botao
                variante="secundaria"
                tamanho="pequeno"
                disabled={novos(tFaltou) === 0}
                onClick={() => aplicar(tFaltou, 'add')}
                title="Nunca bipados com saldo + divergentes na última contagem fechada"
                className={cn(faltou.length === 0 && 'opacity-60')}
              >
                <Undo2 className="h-3.5 w-3.5" />
                O que faltou ({numero(novos(tFaltou))})
              </Botao>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
